import { LoadState, stateInitial, stateLoaded } from "@shared/models/state";
import { defineStore } from "pinia";
import { computed, onBeforeMount, ref } from "vue";
import { localStorageMachineRestrictionKey } from "../constants";
import { loadResource } from "@shared/util/loadResource";
import {
  MachineRestrictionResponse,
  searchMachineRestrictions,
} from "@shared/services/settings";

export const useMachineRestrictionStore = defineStore(
  "machineRestrictionsStore",
  () => {
    // Machine restrictions

    const installedMachineRestrictionsLoadState = ref<
      LoadState<MachineRestrictionResponse[]>
    >({ ...stateInitial });

    onBeforeMount(() => {
      if (installedMachineRestrictionsLoadState.value.loaded) {
        // Don't reload
        return;
      }
      const machineRestrictionsString = localStorage.getItem(
        localStorageMachineRestrictionKey,
      );
      if (machineRestrictionsString === null) {
        installedMachineRestrictionsLoadState.value = {
          ...stateLoaded,
          result: [],
        };
        return;
      }
      try {
        const machineRestrictions = JSON.parse(machineRestrictionsString);
        if (
          Array.isArray(machineRestrictions) &&
          machineRestrictions.length > 0
        ) {
          loadResource(
            () =>
              searchMachineRestrictions({
                page: 1,
                pageSize: machineRestrictions.length,
                uuids: machineRestrictions,
              }),
            installedMachineRestrictionsLoadState,
          );
        } else {
          installedMachineRestrictionsLoadState.value = {
            ...stateLoaded,
            result: [],
          };
        }
      } catch (e) {
        installedMachineRestrictionsLoadState.value = {
          ...stateLoaded,
          result: [],
        };
        return;
      }
    });

    /**
     * Install a machine restriction on the current machine
     */
    function installMachineRestriction(
      restriction: MachineRestrictionResponse,
    ) {
      // Don't install restrictions before load, otherwise existing restrictions might be uninstalled
      if (installedMachineRestrictionsLoadState.value.loaded) {
        // Remove any machine restrictions with the same template
        const updatedArray = installedMachineRestrictionsLoadState.value.result
          .filter((item) => item.templateId !== restriction.templateId)
          .map((item) => item.uuid);
        updatedArray.push(restriction.uuid);
        localStorage.setItem(
          localStorageMachineRestrictionKey,
          JSON.stringify(updatedArray),
        );

        installedMachineRestrictionsLoadState.value.result.splice(
          installedMachineRestrictionsLoadState.value.result.findIndex(
            (item) => item.templateId === restriction.templateId,
          ),
          1,
        );

        installedMachineRestrictionsLoadState.value.result.push(restriction);
      }
    }

    /**
     * Uninstall a machine restriction on the current machine
     */
    function uninstallMachineRestriction(id: string) {
      // Don't uninstall restrictions before load
      if (installedMachineRestrictionsLoadState.value.loaded) {
        const installed = installedMachineRestrictionsLoadState.value.result;
        const updatedArray = installed.map((item) => item.uuid);

        updatedArray.splice(updatedArray.indexOf(id), 1);

        localStorage.setItem(
          localStorageMachineRestrictionKey,
          JSON.stringify(updatedArray),
        );
        const index = installed.findIndex((item) => item.uuid === id);

        installedMachineRestrictionsLoadState.value.result.splice(
          installedMachineRestrictionsLoadState.value.result.findIndex(
            (item) => item.uuid === id,
          ),
          1,
        );
      }
    }

    const loading = computed(
      () => installedMachineRestrictionsLoadState.value.loading,
    );

    const loaded = computed(
      () => installedMachineRestrictionsLoadState.value.loaded,
    );

    const error = computed(
      () => installedMachineRestrictionsLoadState.value.error,
    );

    const result = computed(
      () => installedMachineRestrictionsLoadState.value.result,
    );

    return {
      installMachineRestriction,
      uninstallMachineRestriction,
      installedMachineRestrictionsLoadState,
      loading,
      loaded,
      error,
      result,
    };
  },
);
